import {
  MAX_POINT_RULE_VALUE,
  MIN_POINT_RULE_VALUE,
  PointStreakConfig,
  SavePointStreakPayload,
} from "../../../models/pointRule";

/** Mismo tope que el backend: el motor mira 52 semanas para atrás. */
export const STREAK_MAX_WEEKS = 52;
export const STREAK_DEFAULT_MILESTONES = 3;
export const STREAK_MAX_MILESTONES = 10;

export interface StreakMilestoneFormValues {
  /** null = hito nuevo, todavía no guardado. */
  id: number | null;
  pointsValue: number | null;
  active: boolean;
  hasAwardedPoints: boolean;
}

export interface StreakFormValues {
  intervalWeeks: number | null;
  displayTitle: string;
  displayDescription: string;
  iconKey: string | null;
  visibleToUser: boolean;
  milestones: StreakMilestoneFormValues[];
}

export interface StreakFormErrors {
  displayTitle?: string;
  intervalWeeks?: string;
  /** Por índice de hito. */
  milestones?: Record<number, string>;
}

const newMilestone = (): StreakMilestoneFormValues => ({
  id: null,
  pointsValue: null,
  active: true,
  hasAwardedPoints: false,
});

const MAX_TEXT_LENGTH = 200;

/** Sin racha configurada arranca con tres hitos (N, 2N y 3N) a completar. */
export const buildInitialStreakForm = (config: PointStreakConfig | undefined): StreakFormValues => {
  const milestones = config?.milestones ?? [];
  const texts = {
    displayTitle: config?.displayTitle ?? "",
    displayDescription: config?.displayDescription ?? "",
    iconKey: config?.iconKey ?? null,
    visibleToUser: config?.visibleToUser ?? true,
  };
  if (milestones.length === 0) {
    return {
      ...texts,
      intervalWeeks: null,
      milestones: Array.from({ length: STREAK_DEFAULT_MILESTONES }, newMilestone),
    };
  }
  return {
    ...texts,
    intervalWeeks: config?.intervalWeeks ?? null,
    milestones: milestones.map((milestone) => ({
      id: milestone.id,
      pointsValue: milestone.pointsValue,
      active: milestone.active,
      hasAwardedPoints: milestone.hasAwardedPoints,
    })),
  };
};

/** El hito i (desde 0) se paga a las (i + 1) · intervalo semanas seguidas. */
export const streakMilestoneWeeks = (intervalWeeks: number | null, index: number): number | null =>
  intervalWeeks === null ? null : intervalWeeks * (index + 1);

export const withNextMilestone = (form: StreakFormValues): StreakFormValues => ({
  ...form,
  milestones: [...form.milestones, newMilestone()],
});

/** Solo se quita un hito nuevo que es el último: los guardados se apagan. */
export const withoutLastNewMilestone = (form: StreakFormValues): StreakFormValues => {
  const last = form.milestones[form.milestones.length - 1];
  if (last === undefined || last.id !== null || form.milestones.length <= 1) return form;
  return { ...form, milestones: form.milestones.slice(0, -1) };
};

/** Las etapas se muestran recién con un intervalo entero entre 1 y 52. */
export const isStreakIntervalValid = (intervalWeeks: number | null): intervalWeeks is number =>
  intervalWeeks !== null &&
  Number.isInteger(intervalWeeks) &&
  intervalWeeks >= 1 &&
  intervalWeeks <= STREAK_MAX_WEEKS;

export const canAddStreakMilestone = (form: StreakFormValues): boolean => {
  const nextWeeks = streakMilestoneWeeks(form.intervalWeeks, form.milestones.length);
  return (
    form.milestones.length < STREAK_MAX_MILESTONES &&
    (nextWeeks === null || nextWeeks <= STREAK_MAX_WEEKS)
  );
};

/** Ya se pagó algún hito: el intervalo queda fijo, cambiarlo movería hitos que ya pagaron. */
export const isStreakIntervalLocked = (form: StreakFormValues): boolean =>
  form.milestones.some((milestone) => milestone.hasAwardedPoints);

const isIntegerInRange = (value: number | null, min: number, max: number): boolean =>
  value !== null && Number.isInteger(value) && value >= min && value <= max;

export const validateStreakForm = (form: StreakFormValues): StreakFormErrors => {
  const errors: StreakFormErrors = {};
  const title = form.displayTitle.trim();
  if (form.visibleToUser && title === "") {
    errors.displayTitle = "Una racha visible necesita un título";
  } else if (title.length > MAX_TEXT_LENGTH) {
    errors.displayTitle = `Hasta ${MAX_TEXT_LENGTH} caracteres`;
  }
  const maxInterval = Math.floor(STREAK_MAX_WEEKS / Math.max(form.milestones.length, 1));
  if (!isIntegerInRange(form.intervalWeeks, 1, STREAK_MAX_WEEKS)) {
    errors.intervalWeeks = `Ingresá un número entero entre 1 y ${STREAK_MAX_WEEKS}`;
  } else if (form.intervalWeeks !== null && form.intervalWeeks > maxInterval) {
    errors.intervalWeeks = `Con ${form.milestones.length} hitos, el intervalo puede ser de hasta ${maxInterval} semanas: el último hito no puede pasar de ${STREAK_MAX_WEEKS}.`;
  }

  const milestoneErrors: Record<number, string> = {};
  form.milestones.forEach((milestone, index) => {
    if (!isIntegerInRange(milestone.pointsValue, MIN_POINT_RULE_VALUE, MAX_POINT_RULE_VALUE)) {
      milestoneErrors[index] =
        milestone.pointsValue === null
          ? `Cargá los puntos del hito ${index + 1}`
          : `Ingresá un número entero entre ${MIN_POINT_RULE_VALUE} y ${MAX_POINT_RULE_VALUE}`;
    }
  });
  if (Object.keys(milestoneErrors).length > 0) errors.milestones = milestoneErrors;
  return errors;
};

export const hasStreakFormErrors = (errors: StreakFormErrors): boolean =>
  errors.displayTitle !== undefined ||
  errors.intervalWeeks !== undefined ||
  Object.keys(errors.milestones ?? {}).length > 0;

export const buildSavePointStreakPayload = (
  form: StreakFormValues,
  tenantId: string
): SavePointStreakPayload | null => {
  if (form.intervalWeeks === null) return null;
  const milestones: SavePointStreakPayload["milestones"] = [];
  for (const milestone of form.milestones) {
    if (milestone.pointsValue === null) return null;
    milestones.push({ pointsValue: milestone.pointsValue, active: milestone.active });
  }
  const description = form.displayDescription.trim();
  return {
    tenantId,
    intervalWeeks: form.intervalWeeks,
    displayTitle: form.displayTitle.trim(),
    displayDescription: description === "" ? null : description,
    iconKey: form.iconKey,
    visibleToUser: form.visibleToUser,
    milestones,
  };
};

/** "Racha de asistencia · 4 semanas": así se titula cada hito en la app. */
export const streakMilestoneTitle = (title: string, weeks: number | null): string => {
  const base = title.trim() === "" ? "Racha" : title.trim();
  return weeks === null ? base : `${base} · ${weeks} ${weeks === 1 ? "semana" : "semanas"}`;
};

/** "Se paga a las 6 semanas seguidas", y el último aclara que se repite. */
export const describeStreakMilestone = (
  intervalWeeks: number | null,
  index: number,
  isLast: boolean
): string => {
  const weeks = streakMilestoneWeeks(intervalWeeks, index);
  if (weeks === null) return "Elegí cada cuántas semanas se cumple un hito";
  const unit = weeks === 1 ? "semana seguida" : "semanas seguidas";
  if (!isLast || intervalWeeks === null) return `Se paga a las ${weeks} ${unit}`;
  return `Se paga a las ${weeks} ${unit}, y después se repite cada ${intervalWeeks} ${
    intervalWeeks === 1 ? "semana" : "semanas"
  } mientras siga la racha`;
};
